/**
 * RouteRegistry — the shell's single JSX route-table renderer (T019).
 *
 * This module is **the only file in `apps/web/src/**\/*.{ts,tsx}` permitted
 * to contain `<Routes>` / `<Route>` JSX** (FR-004). The T044 architecture
 * check (`apps/web/src/tests/architecture/no-flat-route-jsx.ts`, Phase 5)
 * whitelists this file by exact path; every other source file that
 * introduces `<Routes>` or `<Route>` JSX MUST fail the check. Tests that
 * exercise react-router flows are covered by the shared ignore rule from
 * T038 (`__tests__` directories); this exclusion applies only to
 * production sources under `apps/web/src`.
 *
 * Routes are data (`./routes.ts`); one renderer interprets each descriptor's
 * `protected` / `role` / `redirect` flags. New routes extend `ROUTES`, while
 * one branch enforces the protected ⇒ `RouteGuard` wrapping invariant.
 *
 * ## Progressive extension
 *
 * `ROUTES` ships with the Cluster-1 entries (Login, Home, Messages,
 * Profile, the `/account → /profile` redirect); 025-admin-area
 * added the `/admin` launch portal, the `/admin/feedback` section, and the
 * `/forbidden` member-on-admin bounce target (the admin descriptors live in
 * the `shell/adminRoutes.ts` fragment, spread into `ROUTES`). Cluster 2/3/4
 * extend the registry by adding descriptors to `shell/routes.ts` (or that
 * fragment) — replacing stubs in place or appending real
 * content/lakikaveri/account surfaces. `RouteRegistry.tsx` itself
 * does not need to change. No fallback `<Route path="*">` is emitted
 * here today; any catch-all entry belongs in `routes.ts`.
 *
 * ## Wrapping shape
 *
 * `RouteDescriptor` is a discriminated union on `kind` (see
 * `./routes.ts`). The renderer switches on the discriminator:
 *
 *   - `kind: 'redirect'` ⇒ render `<Layout><Navigate to={redirect}
 *     replace /></Layout>`. The destination route owns any session/role
 *     guarding; the wrapping `<Layout>` is the chrome painted around
 *     the redirect site until react-router resolves to the destination
 *     descriptor. Used by `/account → /profile` per D-048. See §"Chrome
 *     dispatch" for why this branch wraps in member chrome and §"Chrome
 *     painting during a bounce" for the painting contract.
 *   - `kind: 'render'` with `protected: true` ⇒
 *     `<RouteGuard role={descriptor.role}>` wraps `<Component />`.
 *     `RouteGuard.role` is optional (`undefined` means "any
 *     authenticated user").
 *   - `kind: 'render'` with `protected: false` ⇒ render `<Component />`
 *     directly.
 *
 * The union excludes `redirect` from render descriptors and `component`
 * from redirects, making a "neither component nor redirect" branch unreachable.
 *
 * ## Port-error-boundary wrap
 *
 * Render descriptors carrying `portBoundary: '<port-name>'` opt into a
 * `<PortErrorBoundary>` wrap inside the guard. The wrap order is
 * `Chrome → RouteGuard → PortErrorBoundary → Component` (TA22
 * port-isolation closure):
 *
 *   1. `Chrome` is outermost — either `<Layout>` (the default member
 *      shell) or `<AdminCmsLayout>` (the slim admin authoring shell
 *      for `chrome: 'cms'` descriptors). Chrome wraps the guard so
 *      the source route's shell stays painted across a guard-driven
 *      `<Navigate>` bounce — see "Chrome painting during a bounce"
 *      below for the precise contract. The same posture held in the
 *      pre-021-cms-A1 composition where `<Layout>` lived above the
 *      route table in `main.tsx`; this refactor moves the wrap
 *      site from the composition root into the registry so the
 *      chrome can be per-descriptor without forking `main.tsx`'s
 *      tree shape.
 *   2. `RouteGuard` runs inside the chrome so an unauthenticated
 *      bounce never mounts the boundary subtree (no wasted boundary
 *      instance, no port read on a session that should be on
 *      `/login` instead).
 *   3. `PortErrorBoundary` wraps the rendered component with
 *      `portName={descriptor.portBoundary}` and a
 *      `<ContentUnavailable />` fallback so a port throw lands on a
 *      Finnish-first surface instead of React's default error UI.
 *
 * Unprotected render descriptors with `portBoundary` set still get the
 * boundary wrap (via the same projection), but no Cluster-1 / Phase-A
 * route currently combines `protected: false` with a `portBoundary`.
 * The eleven content-port-backed descriptors that opt in are
 * documented in `./routes.ts`.
 *
 * ## Suspense boundary
 *
 * Every rendered route element is wrapped in `<Suspense>` with a
 * `<RouteLoadingFallback>` (Finnish-first, `role="status"`). This is
 * required because the admin subtree (`/admin`, `/admin/cms/*`) is
 * loaded via `React.lazy()` + a dynamic `import()` so Vite + Rollup
 * emit it as separate chunk(s) excluded from the member entry bundle
 * (FR-018, toward SC-005; see `./routes.ts` §"Admin subtree
 * code-split seam"). A lazy component suspends on first render until
 * its chunk arrives; React needs a `Suspense` ancestor to show a
 * fallback meanwhile.
 *
 * `<Suspense>` is nested INSIDE the chrome wrapper (and inside the
 * guard, when present), making the wrap order
 * `Chrome → RouteGuard → PortErrorBoundary → Suspense → Component`.
 * Two consequences:
 *
 *   1. The route's chrome (`<AdminCmsLayout>` / `<Layout>`) stays
 *      painted around the loading fallback, so the admin shell does not
 *      blank out while its chunk loads.
 *   2. The guard runs above Suspense, so an unauthenticated or
 *      wrong-role visitor bounces (`<Navigate>`) BEFORE the lazy
 *      component mounts: the admin chunk is never fetched for a
 *      session that should not see it.
 *
 * Wrapping every route uniformly (not just the lazy ones) is
 * deliberate and behaviour-preserving: eager components never suspend,
 * so their Suspense boundary is inert and the fallback never shows. A
 * per-descriptor `lazy?` flag to wrap selectively would add a branch
 * with no observable benefit. This mirrors the in-file Suspense
 * wrapper in `primitives/RichTextEditor.tsx`.
 *
 * ## Chrome dispatch
 *
 * `descriptor.chrome === 'portal-home'` mounts `<AdminPortalHomeLayout>`
 * (the bandless launch-portal springboard — `/admin` only); `=== 'portal'`
 * mounts `<AdminPortalLayout>` (the slim "Ylläpito" band shared by the
 * portal-styled sections, e.g. `/admin/feedback`); `=== 'cms'` mounts
 * `<AdminCmsLayout>`; `=== 'bare'` mounts NO chrome wrapper at all — the
 * guarded element renders directly as a standalone full-viewport surface (the
 * auth terminal screens at `/ei-jasenyytta` and `/tunnistus-epaonnistui`, 032
 * US2), so no member shell renders behind them (D-130); every other render
 * descriptor (omitted `chrome`) mounts `<Layout>`. The admin variants share the
 * same one-axis dispatch — 025-admin-area added the `'portal'` + `'portal-home'`
 * branches alongside 021-cms's `'cms'`, and 032 US2 added `'bare'`. The launch
 * portal home (`/admin`) selects
 * `'portal-home'` (a clean centered springboard, no band, matching the
 * proto); its portal-styled sections (e.g. `/admin/feedback`) select
 * `'portal'`, with no registry edit per new section. Redirect descriptors
 * do not carry `chrome` (the discriminated union excludes it from the
 * `redirect` branch); they wrap their `<Navigate>` in `<Layout>` because
 * the only redirect today (`/account → /profile`) is a member-to-member
 * bounce. If a future redirect needs a different chrome (e.g. an admin→admin
 * bounce), the `RouteRedirectDescriptor` type would extend to carry a
 * `chrome` flag — that extension is YAGNI today and not added speculatively.
 * The chrome decision is a
 * per-descriptor projection, not a render-time `useLocation` branch:
 * this keeps the chrome stable across the descriptor's lifetime and
 * avoids the chrome flashing between variants if react-router's
 * path-matching internals re-evaluate during a navigation.
 *
 * ## Chrome painting during a bounce
 *
 * Chrome wraps every `<Navigate>` site (guard bounce, redirect
 * descriptor) so the user never sees a bare `<Navigate>` element
 * between a navigation request and its resolution. The precise
 * contract:
 *
 *   1. Normal render — the route's matched descriptor mounts and its
 *      chrome paints around the rendered component. Stable case.
 *   2. Guard-driven bounce — when `RouteGuard` decides to redirect
 *      via `<Navigate>`, the chrome that paints around that
 *      `<Navigate>` is the SOURCE descriptor's chrome (because that
 *      is the wrap currently mounted at the guard's call site). For
 *      same-chrome bounces (member → member, e.g. an expired member
 *      session bouncing to `/login` — both `<Layout>`) the wrap is
 *      identity and React's reconciliation reuses the chrome
 *      instance. For cross-chrome bounces (`/admin/cms/*` →
 *      `/login`, source `<AdminCmsLayout>` to destination
 *      `<Layout>`) there is a brief same-frame intermediate state
 *      where the source chrome paints around the bare `<Navigate>`
 *      before react-router resolves to the destination descriptor
 *      and the destination chrome takes over. The intermediate is
 *      synchronous within the same render commit so it is typically
 *      invisible — but the structural posture is "source chrome
 *      paints first, then destination chrome", not "destination
 *      chrome paints across the bounce".
 *   3. Redirect descriptor — a `kind: 'redirect'` descriptor wraps
 *      its `<Navigate>` in `<Layout>`. That wrap is the chrome
 *      around the redirect's bare `<Navigate>` until react-router
 *      resolves to the destination descriptor; for the only redirect
 *      today (`/account → /profile`, both member), the wrap is
 *      identity to the destination chrome.
 *
 * The structural rationale for chrome-outermost is unchanged: keeping
 * the wrap site outside the guard preserves visual frame coherency
 * across the navigation commit (no flash of bare `<Navigate>` or empty
 * `<body>` between source and destination chrome), and the per-route
 * `<Layout>` instances reuse via React's same-type-same-position
 * reconciliation rule — see `RouteRegistry.test.tsx`'s "preserves the
 * Layout instance across member→member navigation" invariant for the
 * empirical proof.
 */

import { type JSX, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AnalyticsRouteReady } from '../analytics/AnalyticsCaptureProvider.js';
import { ContentUnavailable } from '../content/routes/ContentUnavailable.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { RouteGuard } from '../session/RouteGuard.js';
import { AdminCmsLayout } from './AdminCmsLayout.js';
import { AdminFinnishLanguageBoundary } from './AdminFinnishLanguageBoundary.js';
import { AdminPortalHomeLayout } from './AdminPortalHomeLayout.js';
import { AdminPortalLayout } from './AdminPortalLayout.js';
import { AdminSectionExitLink } from './AdminSectionExitLink.js';
import { Layout } from './Layout.js';
import { MemberBackHistory } from './MemberBackHistory.js';
import { PortErrorBoundary } from './PortErrorBoundary.js';
import { ROUTES, type RouteDescriptor } from './routes.js';

const fallbackClasses = 'p-4 text-sm text-muted-foreground';

/**
 * Suspense fallback shown while a lazily code-split route chunk loads.
 *
 * Only the admin subtree (`/admin`, `/admin/cms/*`) is `React.lazy` (the
 * FR-018 code-split seam in `./routes.ts`); every other route is an
 * eager import that never suspends, so this fallback is invisible for
 * them. The fallback paints INSIDE the route's chrome wrapper
 * (`<AdminCmsLayout>` / `<Layout>`) because `<Suspense>` is nested below
 * the chrome in `renderRouteElement`, so the shell stays painted while
 * the admin chunk arrives.
 *
 * `role="status"` exposes it as an ARIA live region so assistive tech
 * announces the transient loading state. Copy is the route-neutral
 * `common.loading` key (Finnish-first per FR-040) so any future lazy
 * route reuses it without a per-route string. Mirrors the
 * `EditorLoadingFallback` shape in `primitives/RichTextEditor.tsx`.
 */
function RouteLoadingFallback(): JSX.Element {
  const t = useTranslate();
  return (
    <div className={fallbackClasses} data-testid="route-loading" role="status">
      {t('common.loading')}
    </div>
  );
}

/**
 * Builds the `element` prop for a single `<Route>` by switching on the
 * descriptor's `kind`. Two branches: redirect descriptors wrap their
 * `<Navigate>` in `<Layout>` (member chrome — the only redirect today,
 * `/account → /profile`, is a member-to-member bounce); render
 * descriptors mount the component, wrapping it in `<Suspense>` (so a
 * lazily code-split route loads gracefully), optionally in
 * `PortErrorBoundary` (when `portBoundary` is set), in `RouteGuard`
 * (when `protected: true`), and finally in the chrome wrapper selected
 * by `descriptor.chrome`. The discriminated union makes exhaustiveness
 * statically verifiable.
 *
 * Wrap order is
 * `Chrome → RouteGuard → PortErrorBoundary → Suspense → Component`.
 * Chrome is outermost so the source route's shell stays painted across
 * a guard-driven `<Navigate>` bounce (the source descriptor's chrome
 * paints around the `<Navigate>` until react-router resolves to the
 * destination descriptor's matched element, at which point the
 * destination's chrome takes over); for redirect descriptors, the
 * wrapping `<Layout>` paints around the bare `<Navigate>` until the
 * destination's matched descriptor takes over. The guard fires before
 * the boundary subtree mounts so an unauthenticated bounce never
 * instantiates the boundary or its child component. `<Suspense>` is
 * innermost so the chrome (and the guard) stay above it: the admin
 * chunk only fetches after the guard admits the session, and the
 * route's chrome stays painted around the loading fallback. See
 * `RouteRegistry.tsx`'s file header §"Port-error-boundary wrap",
 * §"Suspense boundary", §"Chrome dispatch", and §"Chrome painting
 * during a bounce" for the full contract.
 */
function renderRouteElement(descriptor: RouteDescriptor): JSX.Element {
  if (descriptor.kind === 'redirect') {
    // Wrap the bare `<Navigate>` in `<Layout>` so the redirect site
    // paints inside member chrome until react-router resolves to the
    // destination descriptor. The only redirect today is
    // `/account → /profile` (both member), so `<Layout>` is the
    // appropriate chrome. A future redirect with a different chrome
    // requirement (e.g. admin→admin) would extend
    // `RouteRedirectDescriptor` with a `chrome` flag — not added
    // speculatively (YAGNI). See file header §"Chrome dispatch".
    return (
      <Layout>
        <Navigate to={descriptor.redirect} replace />
      </Layout>
    );
  }

  const {
    component: Component,
    protected: isProtected,
    role,
    portBoundary,
    chrome,
    requiredCapability,
    analyticsPageKey,
  } = descriptor;

  // Innermost wrap: `<Suspense>` around the rendered component so a
  // lazily code-split route (the admin subtree, FR-018; see
  // `./routes.ts`) loads gracefully WITH the route's chrome already
  // painted. Suspense is nested below chrome (and below the guard, when
  // present), so: (a) the chrome shell stays visible while the admin
  // chunk arrives, and (b) the guard's allow/bounce decision runs
  // BEFORE Suspense mounts, so an unauthenticated visitor bounces to
  // `/login` without ever triggering the admin chunk fetch. Eager
  // routes never suspend, so this boundary is a no-op for them and the
  // fallback never shows: existing behaviour is unchanged. See file
  // header §"Suspense boundary".
  const componentElement = (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Component />
      {analyticsPageKey === undefined ? null : <AnalyticsRouteReady pageKey={analyticsPageKey} />}
    </Suspense>
  );

  // Inner wrap: `PortErrorBoundary` if the descriptor opted in. The
  // boundary's `fallback` is the Finnish-first `<ContentUnavailable />`
  // surface; the boundary owns its own developer-facing diagnostic
  // region (`data-testid="port-error-boundary-diagnostic"`).
  const innerElement =
    portBoundary !== undefined ? (
      <PortErrorBoundary portName={portBoundary} fallback={<ContentUnavailable />}>
        {componentElement}
      </PortErrorBoundary>
    ) : (
      componentElement
    );

  // `adminSignInUrl` activates `RouteGuard`'s anonymous-admin -> server
  // `/admin/login?returnTo=` redirect (spec 023, D-106 / T045). It is safe to
  // wire now because the live `ports.session` is the dual-audience composite
  // whose admin source (`RealSessionProvider`) can read the minted
  // `__Secure-oma_session` cookie; under the old demo-only provider this
  // redirect would have looped (the demo provider cannot read the cookie), so
  // the guard kept it dormant until this cutover. The guard only applies the
  // redirect on the anonymous-on-`role: 'admin'` branch; every other guarded
  // case keeps its historical `/login` bounce. The server's `returnTo.ts`
  // allowlist validates the encoded path on the callback return (no
  // client-side reimplementation, P7).
  // Default-deny membership gating (028-membership-state-gating, research
  // Decision 9): every `role: 'member'` route requires a membership capability.
  // A route that declares its own `requiredCapability` (a carve-out, e.g.
  // `/messages` → `open_messages`) keeps it; every other member route defaults
  // to `other_actions` (greyed for non-valid members). Non-member routes (Home,
  // which is role-undefined, and the `role: 'admin'` admin surfaces) are not
  // membership-gated — admin carries no membership context (spec 023 FR-023).
  const effectiveCapability =
    requiredCapability ?? (role === 'member' ? 'other_actions' : undefined);

  const guardedElement = isProtected ? (
    <RouteGuard role={role} adminSignInUrl="/admin/login" requiredCapability={effectiveCapability}>
      {innerElement}
    </RouteGuard>
  ) : (
    innerElement
  );

  // Shell chrome: 'portal-home' is the bandless admin springboard, 'portal'
  // and 'cms' use their section layouts, and the default uses member Layout.
  // 'bare' renders public auth terminal screens without shell chrome (D-130).
  // Admin chrome and its descendants share the Finnish language boundary.
  if (chrome === 'bare') {
    return guardedElement;
  }
  const ChromeWrapper =
    chrome === 'portal'
      ? AdminPortalLayout
      : chrome === 'portal-home'
        ? AdminPortalHomeLayout
        : chrome === 'cms'
          ? AdminCmsLayout
          : Layout;
  // `AdminPortalLayout`'s `backToPortal` slot is supplied here, at the only
  // mount site that has the chrome discriminant: every `chrome: 'portal'`
  // route is a portal-styled SECTION, so each one gets the Päävalikko exit.
  // The bandless portal home (`chrome: 'portal-home'`) is a different
  // component and never reaches the slot, so the layout's "no affordance
  // unless supplied" contract stays intact.
  const chromeElement =
    chrome === 'portal' ? (
      <AdminPortalLayout backToPortal={<AdminSectionExitLink />}>
        {guardedElement}
      </AdminPortalLayout>
    ) : (
      <ChromeWrapper>{guardedElement}</ChromeWrapper>
    );
  return chrome === 'portal' || chrome === 'portal-home' || chrome === 'cms' ? (
    <AdminFinnishLanguageBoundary>{chromeElement}</AdminFinnishLanguageBoundary>
  ) : (
    chromeElement
  );
}

export function RouteRegistry(): JSX.Element {
  return (
    <MemberBackHistory>
      <Routes>
        {ROUTES.map((descriptor) => (
          <Route
            key={descriptor.path}
            path={descriptor.path}
            element={renderRouteElement(descriptor)}
          />
        ))}
      </Routes>
    </MemberBackHistory>
  );
}
